import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { invitationsApi } from '../api/endpoints';
import { useAuth } from '../hooks/useAuth';
import { AuthShell } from './AuthShell';
import { SsoButtons } from './SsoButtons';
import './LoginPage.css';
import './InviteAcceptPage.css';

// Matches the BE floor (PasswordTooShortError at < 8); we validate client-side
// so the common case never round-trips just to be told the password is short.
const MIN_PASSWORD = 8;

/**
 * Public invite-accept page (SC-2791) — where an invited user lands from the
 * magic link (`/invite/accept?token=…`), outside the authed shell.
 *
 * SSO-first: the pending invite is honored by the IdP-verified email through
 * the normal SSO flow (SC-2796), so "Continue with Google/Microsoft" needs no
 * token — it's the same nav as login, landing on /auth/callback. The email +
 * password path is the secondary fallback for a brand-new non-SSO account; it
 * POSTs the token (the credential) and, on success, gets a full session back.
 *
 * There is no public endpoint that resolves a token to org/role without
 * consuming it, so the page can't name the org before the user acts — the
 * landing stays generic and the session's org is only known after accept.
 */
export function InviteAcceptPage() {
  const { completeInviteAccept } = useAuth();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  // A terminal message means the invite itself is gone (unknown / used /
  // revoked / expired / already a member, or a link with no token) — no
  // resubmit from this page can change that, so we swap the form for the
  // reason + a route to sign-in rather than a dead "try again".
  const [terminal, setTerminal] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!token) {
    return (
      <TerminalPanel message="This invitation link is missing its token. Ask your administrator to resend the invite." />
    );
  }

  if (terminal) {
    return <TerminalPanel message={terminal} />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    setSubmitting(true);
    try {
      const session = await invitationsApi.accept({
        token,
        name: name.trim(),
        password,
      });
      // Hydrates the session and routes to where a fresh login lands — this page
      // unmounts, so there's no post-success state to render here.
      completeInviteAccept(session);
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        // Recoverable validation (name/password required or too short) — keep
        // the form so they can fix it and resubmit.
        setError(err.detail);
        setSubmitting(false);
      } else if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        // Any OTHER 4xx — 404 not-found, 409 not-pending / expired /
        // already-member, or a future 410 Gone — is a permanent rejection of
        // this invite: no resubmit can revive it. Surface the BE's reason and
        // route to sign-in rather than leaving a dead retry. (422 is the one
        // 4xx that IS recoverable, handled above.)
        setTerminal(err.detail);
      } else {
        // 5xx / network / non-ApiError — transient; keep the form so a retry
        // (which CAN change the outcome) stays available.
        setError('Something went wrong. Please try again.');
        setSubmitting(false);
      }
    }
  };

  return (
    <AuthShell>
      <div className="login-form-inner">
        <h1 className="login-form-heading">Accept your invitation</h1>
        <p className="invite-intro">
          You’ve been invited to EKOM. Continue with your work account to join
          your team.
        </p>

        {error && <div className="login-error">{error}</div>}

        <SsoButtons setError={setError} disabled={submitting} />

        <div className="login-divider">
          <span>or</span>
        </div>

        {!showPassword ? (
          <button
            type="button"
            className="invite-password-toggle"
            onClick={() => setShowPassword(true)}
          >
            Set up with email &amp; password instead
          </button>
        ) : (
          <form className="invite-password-form" onSubmit={handleSubmit} noValidate>
            <div className="login-field-wrap">
              <label className="login-field-label" htmlFor="invite-name">
                Name
              </label>
              <input
                id="invite-name"
                className="login-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                type="text"
                placeholder="Your name"
                autoComplete="name"
                required
                disabled={submitting}
              />
            </div>

            <div className="login-field-wrap">
              <label className="login-field-label" htmlFor="invite-password">
                Password
              </label>
              <input
                id="invite-password"
                className="login-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                placeholder="••••••••••"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD}
                disabled={submitting}
              />
            </div>

            <button
              type="submit"
              className="login-btn-signin"
              disabled={submitting}
            >
              {submitting ? 'Accepting…' : 'Accept invitation'}
            </button>
          </form>
        )}
      </div>
    </AuthShell>
  );
}

// A dead-end invite: show why, and offer the one action that can help — signing
// in (e.g. an already-a-member invitee just needs their existing account).
function TerminalPanel({ message }: { message: string }) {
  return (
    <AuthShell>
      <div className="login-form-inner">
        <h1 className="login-form-heading">Invitation unavailable</h1>
        <p className="invite-intro">{message}</p>
        <Link className="invite-signin-link" to="/login">
          Go to sign in
        </Link>
      </div>
    </AuthShell>
  );
}
